import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Send, User, Sparkles, AlertCircle, Wrench, RotateCcw, Camera, X, ImageIcon, Lightbulb } from 'lucide-react';
import { chatService } from '../services/chatService';
import maintenanceService from '../services/maintenanceService';
import { billingService } from '../services/billingService';
import { cn } from '../lib/utils';

// ── Thinking bubble — shown optimistically while agent is processing ──────────
const THINKING_LABELS = [
  'Searching your records…',
  'Checking your portfolio…',
  'Looking that up…',
  'One moment…',
];

const ThinkingBubble = () => {
  const [labelIdx, setLabelIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setLabelIdx(i => (i + 1) % THINKING_LABELS.length), 2000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span className="text-[12px] text-gray-500">{THINKING_LABELS[labelIdx]}</span>
    </div>
  );
};

// ── "Did You Know?" tip pool ──────────────────────────────────────────────────
const NIVA_TIPS = [
  {
    text: 'You can check your lease end date anytime, just ask Niva.',
    prompt: 'When does my lease end?',
    label: 'Ask about lease',
  },
  {
    text: 'Update your phone number in seconds by telling Niva your new number.',
    prompt: 'I want to update my phone number.',
    label: 'Update phone',
  },
  {
    text: 'Once a repair is done, tell Niva to mark the case as resolved.',
    prompt: 'I want to mark a maintenance issue as resolved.',
    label: 'Mark resolved',
  },
  {
    text: 'Control how Niva contacts you, turn off SMS or email anytime.',
    prompt: 'I want to change my notification preferences.',
    label: 'Manage notifications',
  },
  {
    text: 'Ask about property amenities or how to reach your management team.',
    prompt: 'How do I contact the property management team?',
    label: 'Contact management',
  },
];

// Picks a tip that hasn't been shown in this browser session yet (rotates through all before repeating)
const SESSION_KEY = 'niva_tip_index';
const getNextTip = () => {
  const idx = parseInt(sessionStorage.getItem(SESSION_KEY) || '0', 10);
  sessionStorage.setItem(SESSION_KEY, String((idx + 1) % NIVA_TIPS.length));
  return NIVA_TIPS[idx % NIVA_TIPS.length];
};

// ── Did You Know tip card ────────────────────────────────────────────────────
const NivaTipCard = ({ onDismiss, onAction }) => {
  const tip = useMemo(() => getNextTip(), []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="mt-3 mx-0.5 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex items-start gap-2.5">
        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-cyan-100 flex items-center justify-center mt-0.5">
          <Lightbulb className="h-3.5 w-3.5 text-cyan-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-cyan-700 uppercase tracking-wide mb-0.5">Did you know?</p>
          <p className="text-xs text-gray-700 leading-relaxed">{tip.text}</p>
          <button
            onClick={() => onAction(tip.prompt)}
            className="mt-1.5 text-[11px] font-semibold text-cyan-600 hover:text-cyan-800 transition-colors"
          >
            {tip.label} →
          </button>
        </div>
        <button
          onClick={onDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors mt-0.5"
          aria-label="Dismiss tip"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

const generateSessionId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * ChatInterface Component
 *
 * Supports two modes:
 * 1. General chat mode (default) - Uses chatService → LangGraph pipeline
 * 2. Maintenance mode - Also uses chatService → LangGraph pipeline (same agent),
 *    with a maintenance-specific header and photo upload support.
 *    Photos are uploaded to GCS first; the returned URLs are forwarded to the agent.
 *
 * Props:
 * - user: Current user object
 * - embedded: Boolean for embedded mode styling
 * - mode: 'general' | 'maintenance' (default: 'general')
 * - onCaseCreated: Callback when maintenance case is created (not yet supported in LangGraph mode)
 * - onConversationCompleted: Callback when the agent finishes the conversation
 * - initialMessage: When provided, auto-sends this message on mount
 */
// ── Markdown renderer components ──────────────────────────────────────────────
// Used for assistant messages. Internal links (starting with /) use React Router.
const MarkdownLink = ({ href, children }) => {
  const navigate = useNavigate();
  const isInternal = href && href.startsWith('/');
  if (isInternal) {
    return (
      <button
        onClick={() => navigate(href)}
        className="text-cyan-600 hover:text-cyan-800 underline underline-offset-2 font-medium transition-colors"
      >
        {children}
      </button>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-800 underline underline-offset-2">
      {children}
    </a>
  );
};

const MARKDOWN_COMPONENTS = {
  a: MarkdownLink,
  table: ({ children }) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full text-[12px] border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-cyan-50">{children}</thead>,
  th: ({ children }) => (
    <th className="px-3 py-1.5 text-left font-semibold text-cyan-800 border border-gray-200 whitespace-nowrap">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-1.5 border border-gray-200 align-top">{children}</td>
  ),
  tr: ({ children }) => <tr className="even:bg-gray-50">{children}</tr>,
  p: ({ children }) => <p className="mb-1 last:mb-0 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-inside space-y-0.5 my-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside space-y-0.5 my-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  code: ({ children }) => <code className="bg-gray-200 px-1 py-0.5 rounded text-[11px] font-mono">{children}</code>,
};

const ChatInterface = ({ user, embedded = false, mode = 'general', onCaseCreated, onConversationCompleted, initialMessage = '' }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  // sessionId is stateful so handleResetConversation can start a fresh LangGraph thread
  const [sessionId, setSessionId] = useState(() => generateSessionId());
  const [error, setError] = useState(null);
  const [conversationComplete, setConversationComplete] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const modeLabel = mode === 'maintenance' ? 'Maintenance Mode' : 'General Mode';
    console.log(`[ChatInterface] Invoked — ${modeLabel} | sessionId=${sessionId} | embedded=${embedded} | user=${user?.username || user?.email || 'unknown'}`);

    if (mode === 'general') {
      loadChatHistory();
    } else {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm Niva. I'll help you submit a maintenance request. Could you describe the issue you're experiencing?",
        timestamp: new Date().toISOString()
      }]);
    }
    setConversationComplete(false);
  }, [sessionId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-send initial message when provided (e.g. from NivaChat welcome screen)
  const initialMessageSentRef = useRef(false);
  useEffect(() => {
    if (initialMessage && !initialMessageSentRef.current && messages.length > 0 && !loading) {
      initialMessageSentRef.current = true;
      setTimeout(() => {
        const sendInitial = async () => {
          setLoading(true);
          setError(null);
          const userMessage = {
            role: 'user',
            content: initialMessage,
            timestamp: new Date().toISOString()
          };
          const thinkingMessage = { role: 'assistant', isThinking: true, timestamp: new Date().toISOString() };
          setMessages(prev => [...prev, userMessage, thinkingMessage]);
          try {
            // Both modes use the LangGraph pipeline via chatService
            const response = await chatService.sendMessage(sessionId, initialMessage, user?.id);
            if (response.limitReached) {
              setMessages(prev => [...prev.filter(m => !m.isThinking), {
                role: 'assistant',
                content: response.upgradeMessage,
                timestamp: new Date().toISOString(),
                isUpgradePrompt: true,
              }]);
            } else {
              setMessages(prev => [...prev.filter(m => !m.isThinking), response.aiMessage]);
              if (response.conversationComplete) {
                setConversationComplete(true);
                setShowTip(true);
                if (onConversationCompleted) {
                  setTimeout(() => onConversationCompleted(response), 800);
                }
              }
              if (response.warning) setError(response.warning);
            }
          } catch (err) {
            console.error('Failed to send initial message:', err);
            setError(err.response?.data?.error || 'Failed to send message. Please try again.');
            setMessages(prev => prev.slice(0, -2));
          } finally {
            setLoading(false);
          }
        };
        sendInitial();
      }, 100);
    }
  }, [initialMessage, messages.length, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      const history = await chatService.getChatHistory(sessionId);
      const prior = history.messages || [];
      if (prior.length === 0) {
        const firstName = (user?.name || user?.username || '').split(' ')[0];
        setMessages([{
          role: 'assistant',
          content: `Hi${firstName ? ` ${firstName}` : ''}! I'm Niva, your AI maintenance assistant. You can report a maintenance issue or ask me anything about your property. How can I help you today?`,
          timestamp: new Date().toISOString(),
        }]);
      } else {
        setMessages(prior);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
      setError('Failed to load chat history');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowed = files.slice(0, 2 - pendingImages.length);
    const newPreviews = allowed.map((f) => URL.createObjectURL(f));

    setPendingImages((prev) => [...prev, ...allowed]);
    setImagePreviewUrls((prev) => [...prev, ...newPreviews]);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setPendingImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    const hasText = inputMessage.trim().length > 0;
    const hasImages = pendingImages.length > 0;
    if ((!hasText && !hasImages) || loading || conversationComplete) return;

    const messageText = inputMessage.trim() || (hasImages ? `Uploaded ${pendingImages.length} photo(s)` : '');
    setInputMessage('');
    setLoading(true);
    setError(null);

    const imagesToUpload = [...pendingImages];
    const previewsToShow = [...imagePreviewUrls];
    setPendingImages([]);
    setImagePreviewUrls([]);

    try {
      const userMessage = {
        role: 'user',
        content: messageText,
        images: previewsToShow.length > 0 ? previewsToShow : undefined,
        timestamp: new Date().toISOString()
      };
      const thinkingMessage = { role: 'assistant', isThinking: true, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, userMessage, thinkingMessage]);

      // Upload photos to GCS first (both modes) and forward URLs to the LangGraph agent
      let imageUrls = null;
      if (imagesToUpload.length > 0) {
        setUploading(true);
        try {
          const uploadResult = await maintenanceService.uploadPhotos(imagesToUpload);
          imageUrls = uploadResult.image_urls;
        } catch (uploadErr) {
          console.error('Photo upload failed:', uploadErr);
          setError('Failed to upload photos. Please try again.');
          setMessages(prev => prev.slice(0, -2));
          setLoading(false);
          setUploading(false);
          return;
        }
        setUploading(false);
      }

      // Both modes route through the LangGraph pipeline via chatService
      const response = await chatService.sendMessage(sessionId, messageText, user?.id, imageUrls);

      // Tier limit reached — show upgrade prompt as an assistant message
      if (response.limitReached) {
        const limitMessage = {
          role: 'assistant',
          content: response.upgradeMessage,
          timestamp: new Date().toISOString(),
          isUpgradePrompt: true,
        };
        setMessages(prev => [...prev.filter(m => !m.isThinking), limitMessage]);
        return;
      }

      setMessages(prev => [...prev.filter(m => !m.isThinking), response.aiMessage]);
      if (response.conversationComplete) {
        setConversationComplete(true);
        setShowTip(true);
        if (onConversationCompleted) {
          setTimeout(() => onConversationCompleted(response), 800);
        }
      }
      if (response.warning) setError(response.warning);

    } catch (error) {
      console.error('Failed to send message:', error);
      setError(error.response?.data?.error || 'Failed to send message. Please try again.');
      setMessages(prev => prev.slice(0, -2));
    } finally {
      setLoading(false);
      setUploading(false);
      inputRef.current?.focus();
    }
  };

  // Reset starts a fresh LangGraph thread by generating a new sessionId (new thread_id)
  const handleResetConversation = () => {
    if (mode !== 'maintenance') return;
    setSessionId(generateSessionId());
    setError(null);
    setPendingImages([]);
    setImagePreviewUrls([]);
    // The useEffect on [sessionId] will set the welcome message automatically
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Maintenance mode header */}
      {mode === 'maintenance' && (
        <div className="flex items-center justify-between p-3 rounded-lg mb-3 flex-shrink-0 bg-cyan-50 text-cyan-800 border border-cyan-200">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            <span className="font-medium text-sm">Maintenance Request</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetConversation}
            disabled={loading || messages.length <= 1}
            className="h-7 text-xs"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            New Request
          </Button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-amber-50 text-amber-800 p-2.5 rounded-lg mb-3 flex items-center gap-2 text-xs flex-shrink-0 border border-amber-200">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Conversation complete */}
      {conversationComplete && (
        <div className="bg-emerald-50 text-emerald-800 p-2.5 rounded-lg mb-3 text-xs flex-shrink-0 border border-emerald-200">
          This conversation is complete. If you need anything else, start a new request.
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto mb-3">
        <div className="flex flex-col gap-3">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground text-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-cyan-100 to-blue-100 mb-3">
                <Sparkles className="h-6 w-6 text-cyan-600" />
              </div>
              <p className="text-sm font-medium text-gray-500">Chat with Niva</p>
              <p className="text-xs text-gray-400 mt-1">Ask anything or report a maintenance issue</p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={index}
                className={cn(
                  "flex gap-2.5 text-sm",
                  message.role === 'user' ? "flex-row-reverse" : "flex-row"
                )}
              >
                {/* Avatar */}
                <div className={cn(
                  "flex items-center justify-center h-7 w-7 rounded-full flex-shrink-0 mt-0.5",
                  message.role === 'user'
                    ? "bg-gray-800"
                    : "bg-gradient-to-br from-cyan-500 to-blue-600"
                )}>
                  {message.role === 'user'
                    ? <User className="h-3.5 w-3.5 text-white" />
                    : <Sparkles className="h-3.5 w-3.5 text-white" />
                  }
                </div>

                {/* Bubble */}
                <div className={cn(
                  "flex flex-col gap-1 max-w-[80%]",
                  message.role === 'user' ? "items-end" : "items-start"
                )}>
                  {(message.role === 'assistant' || message.isThinking) && (
                    <span className="text-[10px] text-gray-400 font-medium ml-1">Niva</span>
                  )}
                  <div className={cn(
                    "px-3 py-2 rounded-2xl",
                    message.isUpgradePrompt
                      ? "bg-amber-50 border border-amber-300 text-amber-900 rounded-bl-md"
                      : message.role === 'user'
                        ? "bg-gray-800 text-white rounded-br-md"
                        : "bg-gray-100 text-gray-800 rounded-bl-md"
                  )}>
                    {message.images && message.images.length > 0 && (
                      <div className="flex gap-1.5 mb-1.5">
                        {message.images.map((src, imgIdx) => (
                          <img
                            key={imgIdx}
                            src={src}
                            alt={`Upload ${imgIdx + 1}`}
                            className="h-20 w-20 object-cover rounded-lg border border-white/20"
                          />
                        ))}
                      </div>
                    )}
                    {message.isThinking ? (
                      <ThinkingBubble />
                    ) : message.isUpgradePrompt ? (
                      <div className="text-[13px] leading-relaxed">
                        <p className="font-semibold mb-1">Message limit reached</p>
                        <p className="mb-2">{message.content}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <button
                            onClick={() => billingService.startCheckout('starter')}
                            className="px-3 py-1 rounded-md bg-amber-700 text-white text-xs font-medium hover:bg-amber-800 transition-colors"
                          >
                            Upgrade to Starter ($29/mo)
                          </button>
                          <button
                            onClick={() => billingService.startCheckout('professional')}
                            className="px-3 py-1 rounded-md bg-amber-800 text-white text-xs font-medium hover:bg-amber-900 transition-colors"
                          >
                            Upgrade to Professional ($79/mo)
                          </button>
                        </div>
                      </div>
                    ) : message.role === 'assistant' ? (
                      <div className="text-[13px] leading-relaxed">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap leading-relaxed text-[13px]">{message.content}</p>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400 mx-1">
                    {formatTimestamp(message.timestamp)}
                  </span>
                </div>
              </div>
            ))
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Did You Know tip — appears after conversation completes */}
        {showTip && (
          <NivaTipCard
            onDismiss={() => setShowTip(false)}
            onAction={(prompt) => {
              setShowTip(false);
              navigate('/niva', { state: { nivaPrompt: prompt } });
            }}
          />
        )}
      </div>

      {/* Image preview strip */}
      {imagePreviewUrls.length > 0 && (
        <div className="flex gap-2 mb-2 flex-shrink-0 px-1">
          {imagePreviewUrls.map((url, idx) => (
            <div key={idx} className="relative group">
              <img
                src={url}
                alt={`Preview ${idx + 1}`}
                className="h-16 w-16 object-cover rounded-lg border border-gray-200"
              />
              <button
                onClick={() => removeImage(idx)}
                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {pendingImages.length < 2 && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="h-16 w-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-cyan-400 hover:text-cyan-500 transition-colors"
            >
              <ImageIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {/* Uploading indicator */}
      {uploading && (
        <div className="text-xs text-cyan-600 mb-1 px-1 flex-shrink-0">Uploading photos...</div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Input area */}
      <div className="flex gap-2 flex-shrink-0">
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={loading || conversationComplete || pendingImages.length >= 2}
          variant="ghost"
          size="icon"
          className="rounded-full h-9 w-9 flex-shrink-0 text-gray-500 hover:text-cyan-600"
          title="Upload photos (max 2)"
        >
          <Camera className="h-4 w-4" />
          <span className="sr-only">Upload photo</span>
        </Button>
        <Input
          ref={inputRef}
          placeholder={conversationComplete ? "Conversation completed" : (mode === 'maintenance' ? "Describe your issue..." : "Message Niva...")}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          disabled={loading || conversationComplete}
          className="flex-1 rounded-full bg-gray-50 border-gray-200 focus-visible:ring-cyan-500 text-sm"
        />
        <Button
          onClick={handleSendMessage}
          disabled={(!inputMessage.trim() && pendingImages.length === 0) || loading || conversationComplete}
          size="icon"
          className="rounded-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 h-9 w-9 flex-shrink-0"
        >
          <Send className="h-4 w-4" />
          <span className="sr-only">Send</span>
        </Button>
      </div>
    </div>
  );
};

export default ChatInterface;
